import { Dialog, DialogContent, DialogTitle, IconButton, Typography, Box, Divider } from '@mui/material';
import { Close } from '@mui/icons-material';
import CustomerModalForm from './CustomerModalForm';
import { useCustomerFormData } from '@/customers/presentation/hooks/useCustomerFormData';
import tid from '@/common/infrastructure/config/tid';
import { BACKGROUND_COLORS, BORDER_COLORS, RGBA_COLORS } from '@/common/presentation/constants/colors';
import { useTheme } from '@mui/material';

interface CustomerModalProps {
  open: boolean;
  selectedCustomerId?: number;
  onClose: () => void;
  onSuccess?: (customer: { id: number; companyName: string }) => void;
}

export default function CustomerModal({ open, onClose, selectedCustomerId, onSuccess }: Readonly<CustomerModalProps>) {
  const theme = useTheme();
  // Only skip queries when modal is closed - this ensures queries run when modal opens
  const { isNew, customer, customerLoading } = useCustomerFormData(selectedCustomerId, !open);

  const formikStatus = { loading: customerLoading };

  const title = isNew ? 'New Customer' : 'Edit Customer';
  const subtitle = isNew
    ? 'Complete the details to create a new Customer'
    : `CUSTOMER ID: #${selectedCustomerId ?? customer?.id ?? 'Loading...'}`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 10
      }}
      slotProps={{
        backdrop: {
          sx: {
            zIndex: (theme) => theme.zIndex.modal + 10
          }
        },
        paper: {
          sx: {
            borderRadius: '12px',
            boxShadow: `0 8px 32px ${RGBA_COLORS.GRAY_12}`,
            maxHeight: '90vh',
            zIndex: (theme) => theme.zIndex.modal + 10
          }
        }
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: `1px solid ${BORDER_COLORS.LIGHT}`,
          pb: 2,
          mb: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: '24px' }}>
            {title}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              textTransform: 'uppercase',
              letterSpacing: '1px',
              color: 'text.primary',
              fontSize: '12px',
              lineHeight: '20px'
            }}
          >
            {subtitle}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: theme.palette.grey[600],
            '&:hover': {
              backgroundColor: BACKGROUND_COLORS.HOVER,
              color: 'text.primary'
            }
          }}
          {...tid('customers:management:close-button')}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ padding: { xs: 2, sm: '20px 24px' }, overflow: 'auto' }}>
        <CustomerModalForm
          loading={customerLoading}
          isNew={isNew}
          initialValues={customer}
          formikStatus={formikStatus}
          onClose={onClose}
          open={open}
          onSuccess={onSuccess}
        />
      </DialogContent>
    </Dialog>
  );
}
